import { useNavigate } from "react-router-dom";
import remaLogo from './remalogo.svg';
import './DataSpaceSimple.scss';
import { useEffect, useState } from "react";
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import ExtensionIcon from '@mui/icons-material/Extension';
import GavelIcon from '@mui/icons-material/Gavel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Radio from '@mui/material/Radio';

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const navItems = [
    { key: "assets",       label: "Assets",        Icon: ExtensionIcon   },
    { key: "contracts",    label: "Contracts",      Icon: GavelIcon       },
    { key: "policies",     label: "Policies",       Icon: ReceiptLongIcon },
    { key: "datatransfer", label: "Data Transfer",  Icon: ChangeCircleIcon},
];

function cleanKey(key) {
    return key
        .replace(/^@/, '')
        .replace(/^edc:/, '')
        .replace(/^https?:\/\/[^#]*[#/]/, '')
        .replace(/_/g, ' ');
}

function DetailValue({ value }) {
    if (value === null || value === undefined)
        return <span style={{ color: "#afafaf", fontSize: "12px" }}>—</span>;
    if (typeof value === 'object')
        return <DetailObject obj={value} />;
    return (
        <div style={{ background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: "5px", padding: "5px 9px", fontSize: "12px", fontFamily: "monospace", color: "#202020", wordBreak: "break-all" }}>
            {String(value)}
        </div>
    );
}

function DetailObject({ obj }) {
    if (Array.isArray(obj)) {
        if (obj.length === 0) return <span style={{ color: "#afafaf", fontSize: "12px" }}>[ ]</span>;
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "8px", borderLeft: "2px solid #ebebeb" }}>
                {obj.map((item, i) => <DetailValue key={i} value={item} />)}
            </div>
        );
    }
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {Object.entries(obj).map(([key, val]) => (
                <div key={key}>
                    <div style={{ fontSize: "10px", fontWeight: "700", color: "#7c7c7c", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "3px" }}>
                        {cleanKey(key)}
                    </div>
                    <DetailValue value={val} />
                </div>
            ))}
        </div>
    );
}

/* ── Search bar shared component ─────────────────────────────────────────── */
function SearchBar({ value, onChange, onSearch, onReset }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <input
                style={{ borderRadius: "6px", padding: "5px 10px", border: "1px solid #d3d3d3", fontSize: "13px", color: "#202020", outline: "none", width: "170px" }}
                value={value || ''}
                onChange={onChange}
                onKeyDown={e => e.key === 'Enter' && onSearch()}
                placeholder="Search…"
            />
            <div style={{ cursor: "pointer", color: "#269044", border: "1px solid rgba(38,144,68,0.4)", padding: "4px 6px", borderRadius: "6px", display: "flex", alignItems: "center" }} onClick={onSearch}>
                <SearchIcon style={{ fontSize: "18px" }} />
            </div>
            <div style={{ cursor: "pointer", color: "#dc3545", border: "1px solid rgba(220,53,69,0.35)", padding: "4px 6px", borderRadius: "6px", display: "flex", alignItems: "center" }} onClick={onReset}>
                <SearchOffIcon style={{ fontSize: "18px" }} />
            </div>
        </div>
    );
}

// Accepts either a bare host/IP (builds http://host:port, the old behaviour)
// or a full URL (used as-is, ignoring port) — lets participants behind an
// HTTPS reverse proxy with path-based routing (no exposed port) set e.g.
// PROVIDER_HOST=https://connector.example.com instead of an IP:port pair.
function buildTarget(hostValue, port) {
    if (!hostValue) return `http://localhost:${port}`;
    if (/^https?:\/\//i.test(hostValue)) {
        return hostValue.replace(/\/+$/, '');
    }
    return `http://${hostValue}:${port}`;
}

function collectShellIdentityCandidates(shell) {
    const candidates = new Set();
    const addCandidate = (value) => {
        if (!value || typeof value !== "string") return;
        const trimmed = value.trim();
        if (!trimmed) return;
        candidates.add(trimmed);
        const withoutHash = trimmed.split('#').pop();
        if (withoutHash) candidates.add(withoutHash);
        const withoutSlash = trimmed.split('/').pop();
        if (withoutSlash) candidates.add(withoutSlash);
    };

    addCandidate(shell?.idShort);
    addCandidate(shell?.identification?.id);
    addCandidate(shell?.asset?.identification?.id);
    (shell?.asset?.keys || []).forEach((key) => addCandidate(key?.value));
    return candidates;
}

/* ── Main component ───────────────────────────────────────────────────────── */
function DataSpaceSimple() {
    const providerHost       = process.env.REACT_APP_PROVIDER_HOST;
    const consumerHost       = process.env.REACT_APP_CONSUMER_HOST;
    const edcManagementPort  = process.env.REACT_APP_EDC_MANAGEMENT_PORT;
    const edcDspPort         = process.env.REACT_APP_EDC_DSP_PORT;
    const edcLegacyPort      = process.env.REACT_APP_EDC_LEGACY_PORT;
    const consumerDspUrl     = process.env.REACT_APP_CONSUMER_DSP_URL || `${buildTarget(consumerHost, edcDspPort)}/api/dsp`;
    const publishServicePort = process.env.REACT_APP_PUBLISH_SERVICE_PORT;
    const servicePublicBaseUrl = process.env.REACT_APP_SERVICE_PUBLIC_BASE_URL;
    const aasHost            = process.env.REACT_APP_AAS_HOST;
    const aasReadPort        = process.env.REACT_APP_AAS_READ_PORT;

    const [aasShells, setAasShells]                 = useState([]);
    const [search, setSearch]                       = useState('');
    const [selectedValue, setSelectedValue]         = useState('a');
    const [assetIdToConnectorIdMap]                 = useState({});
    const navigate = useNavigate();

    const [assets, setAssets]                       = useState([]);
    const [originalAssets, setOriginalAssets]       = useState([]);
    const [catalog, setCatalog]                     = useState([]);
    const [originalCatalog, setOriginalCatalog]     = useState([]);
    const [contracts, setContracts]                 = useState([]);
    const [originalContracts, setOriginalContracts] = useState([]);
    const [policies, setPolicies]                   = useState([]);
    const [change, setChange]                       = useState(false);
    const [whereWeAt, setWhereWeAt]                 = useState("assets");
    const [details, setDetails]                     = useState(null);
    const [detailsHead, setDetailsHead]             = useState("");
    const [completedTransfers, setCompletedTransfers] = useState([]);
    const [catalogError, setCatalogError]           = useState(false);
    const [catalogLoading, setCatalogLoading]       = useState(true);
    const [contractsLoading, setContractsLoading]   = useState(true);
    const [contractsError, setContractsError]       = useState(false);
    const [selectedItem, setSelectedItem]           = useState(null);
    const [hoveredItem, setHoveredItem]             = useState(null);

    const handleRadioChange = (event) => {
        setSelectedValue(event.target.value);
        setCatalog(originalCatalog);
    };

    useEffect(() => {
        fetchContracts(); fetchPolicies(); fetchAssets(); fetchCatalog();
        fetch(`/aasServer/shells`)
            .then(r => r.json())
            .then(data => setAasShells(Array.isArray(data) ? data : []))
            .catch(err => console.warn("[AAS] could not fetch shells:", err));
    }, [change]);

    useEffect(() => { fetchTransfers(); }, []);

    function isCatalogItemDone(catalogAssetId) {
        return aasShells.some(shell => collectShellIdentityCandidates(shell).has(catalogAssetId));
    }

    const handleClick = (section) => {
        setWhereWeAt(section);
        setSelectedItem(null);
        setDetails(null);
        setDetailsHead("");
        fetchTransfers();
    };

    /* ── Search handlers ── */
    function submitSearch() {
        if (!search) { setAssets(originalAssets); return; }
        setAssets(originalAssets.filter(item => item.assetId?.includes(search.toLowerCase())));
    }
    function submitSearchContracts() {
        if (!search) { setContracts(originalContracts); return; }
        setContracts(originalContracts.filter(item => item.asset?.assetId?.includes(search)));
    }
    function submitSearchDataTransfer() {
        const key = selectedValue === 'a' ? 'asset.assetId' : 'participantId';
        const filtered = originalCatalog.filter(item => {
            const val = selectedValue === 'a' ? item.asset?.assetId : item.participantId;
            return val?.toLowerCase().includes(search.toLowerCase());
        });
        if (filtered.length > 0) { setCatalog(filtered); return; }
        Swal.fire({ title: 'No results', text: 'No matching items found', icon: 'error', confirmButtonText: 'OK' });
        setCatalog(originalCatalog); setSearch('');
    }
    function resetCatalog() { setCatalog(originalCatalog); setSearch(''); }

    /* ── Transfer ── */
    async function letsDoTheTransfer(e, catalogItem) {
        const currentID = catalogItem.asset?.assetId;
        let contractAgreementId = null;
        try {
            const res = await fetch(`/provider-api/api/management/wrapper/ui/pages/contract-agreement-page`, { method: "POST", headers: { "x-api-key": "ApiKeyDefaultValue" } });
            const data = await res.json();
            const match = (data.contractAgreements || []).find(a => a.asset?.assetId === currentID);
            if (match) contractAgreementId = match.contractAgreementId;
        } catch (err) { console.warn("[letsDoTheTransfer] could not fetch agreements:", err); }

        if (!contractAgreementId) {
            const contractOffer = catalogItem.contractOffers?.[0];
            const rawPolicy = contractOffer?.policy;
            const policyJsonLd = rawPolicy?.policyJsonLd ?? JSON.stringify(rawPolicy ?? {});
            const negotiationBody = { assetId: currentID, contractOfferId: contractOffer?.contractOfferId, counterPartyAddress: consumerDspUrl, counterPartyParticipantId: catalogItem.participantId || catalogItem.originatorId, policyJsonLd };
            const negRes = await fetch(`/provider-api/api/management/wrapper/ui/pages/catalog-page/contract-negotiations`, { method: "POST", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" }, body: JSON.stringify(negotiationBody) });
            const negData = await negRes.json();
            if (!negRes.ok) { Swal.fire({ title: 'Negotiation Failed', text: `Status ${negRes.status}.`, icon: 'error', confirmButtonText: 'OK' }); return; }
            const negotiationId = negData.contractNegotiationId;
            Swal.fire({ title: 'Negotiating Contract…', text: 'Waiting for contract to be finalised…', icon: 'info', showConfirmButton: false, didOpen: () => Swal.showLoading() });
            for (let attempt = 0; attempt < 12; attempt++) {
                await new Promise(r => setTimeout(r, 2500));
                const pollData = await (await fetch(`/provider-api/api/management/v2/contractnegotiations/${negotiationId}`, { headers: { "x-api-key": "ApiKeyDefaultValue" } })).json();
                const state = pollData['edc:state'] || pollData.state;
                const agreementId = pollData['edc:contractAgreementId'] || pollData.contractAgreementId;
                if (agreementId && String(state).includes("FINALIZED")) { contractAgreementId = agreementId; Swal.close(); break; }
                if (["TERMINATED", "ERROR"].includes(state)) { Swal.fire({ title: 'Negotiation Terminated', icon: 'error', confirmButtonText: 'OK' }); return; }
            }
            if (!contractAgreementId) { Swal.fire({ title: 'Negotiation Timeout', icon: 'warning', confirmButtonText: 'OK' }); return; }
        }

        const sinkBaseUrl = servicePublicBaseUrl
            ? `${servicePublicBaseUrl.replace(/\/+$/, '')}/asset_transfer`
            : `${buildTarget(providerHost, publishServicePort)}/asset_transfer`;
        const transferBody = { contractAgreementId, dataSinkProperties: { "https://w3id.org/edc/v0.0.1/ns/baseUrl": sinkBaseUrl, "https://w3id.org/edc/v0.0.1/ns/contentType": "application/json", "https://w3id.org/edc/v0.0.1/ns/method": "POST", "https://w3id.org/edc/v0.0.1/ns/queryParams": "", "https://w3id.org/edc/v0.0.1/ns/type": "HttpData" }, transferProcessProperties: {} };
        const transferRes = await fetch(`/provider-api/api/management/wrapper/ui/pages/contract-agreement-page/transfers`, { method: "POST", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" }, body: JSON.stringify(transferBody) });
        const transferData = await transferRes.json();
        if (!transferRes.ok) { Swal.fire({ title: 'Transfer Failed', icon: 'error', confirmButtonText: 'OK' }); return; }
        const transferProcessId = transferData.id || transferData.transferProcessId || transferData['edc:transferProcessId'] || transferData['@id'];
        if (!transferProcessId) { Swal.fire({ title: 'Transfer Started', icon: 'info', confirmButtonText: 'OK' }); fetchTransfers(); return; }
        Swal.fire({ title: 'Transferring Data…', text: 'Waiting for transfer to complete…', icon: 'info', showConfirmButton: false, didOpen: () => Swal.showLoading() });
        for (let attempt = 0; attempt < 20; attempt++) {
            await new Promise(r => setTimeout(r, 2500));
            const tpData = await (await fetch(`/provider-api/api/management/v2/transferprocesses/${transferProcessId}`, { headers: { "x-api-key": "ApiKeyDefaultValue" } })).json();
            const stateStr = String(typeof tpData['edc:state'] === 'object' ? JSON.stringify(tpData['edc:state']) : (tpData['edc:state'] || tpData.state || '')).toUpperCase();
            if (stateStr.includes('COMPLETED')) { Swal.fire({ title: 'Transfer Completed', icon: 'success', confirmButtonText: 'OK' }); setChange(c => !c); fetchTransfers(); return; }
            if (stateStr.includes('TERMINATED') || stateStr.includes('ERROR')) { Swal.fire({ title: 'Transfer Failed', icon: 'error', confirmButtonText: 'OK' }); return; }
        }
        Swal.fire({ title: 'Transfer Timeout', icon: 'warning', confirmButtonText: 'OK' }); fetchTransfers();
    }

    async function fetchTransfers() {
        const myRes = await (await fetch(`/provider-api/api/management/wrapper/ui/pages/transfer-history-page`, { headers: { "x-api-key": "ApiKeyDefaultValue" } })).json();
        const transferList = myRes.transferEntries || [];
        const catalogIds = new Set(catalog.map(item => item.asset.assetId));
        const isCompleted = s => { if (!s) return false; return (typeof s === 'object' ? JSON.stringify(s) : String(s)).toUpperCase().includes('COMPLETED'); };
        setCompletedTransfers(transferList.filter(e => catalogIds.has(e.assetId) && isCompleted(e.state)).map(e => e.assetId));
    }

    /* ── JSX ─────────────────────────────────────────────────────────────── */
    return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", backgroundColor: "#fff", borderBottom: "1px solid #d3d3d3", height: "60px", flexShrink: 0, zIndex: 1030, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "0 24px" }}>
                <img src={remaLogo} style={{ height: "26px" }} alt="RemaNet" />
                <div style={{ width: "1px", height: "22px", background: "#e0e0e0", flexShrink: 0 }} />
                <span style={{ fontSize: "13px", color: "#7c7c7c", fontWeight: "500", letterSpacing: "0.02em" }}>DataSpace Explorer</span>
            </div>

            {/* Nav tabs */}
            <div style={{ display: "flex", backgroundColor: "#fff", borderBottom: "1px solid #d3d3d3", padding: "0 16px", flexShrink: 0 }}>
                <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#7c7c7c", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid transparent", marginBottom: "-1px" }} onClick={() => navigate("/")}>DT Explorer</button>
                <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#7c7c7c", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid transparent", marginBottom: "-1px" }} onClick={() => navigate("/sync")}>Product DT-DS</button>
                <button style={{ fontSize: "13px", fontWeight: "600", cursor: "pointer", color: "#269044", padding: "0 16px", height: "40px", background: "none", border: "none", borderBottom: "2px solid #269044", marginBottom: "-1px" }} onClick={() => navigate("/dataspace")}>DS Management</button>
            </div>

            {/* Body */}
            <div style={{ display: "flex", flex: 1, overflow: "hidden", background: "#ebebeb", gap: "1px" }}>

                {/* ── Sidebar ── */}
                <div style={{ width: "190px", minWidth: "170px", background: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
                    <div style={{ padding: "14px 16px 10px", fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c", borderBottom: "1px solid #ebebeb", flexShrink: 0 }}>
                        DS Management
                    </div>
                    <div style={{ padding: "8px 8px", display: "flex", flexDirection: "column", gap: "2px" }}>
                        {navItems.map(({ key, label, Icon }) => {
                            const isActive = whereWeAt === key;
                            return (
                                <div
                                    key={key}
                                    onClick={() => handleClick(key)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: "9px",
                                        padding: "9px 12px", borderRadius: "7px", cursor: "pointer",
                                        background: isActive ? "rgba(38,144,68,0.08)" : "transparent",
                                        borderLeft: isActive ? "3px solid #269044" : "3px solid transparent",
                                        color: isActive ? "#269044" : "#606060",
                                        fontWeight: isActive ? "600" : "500",
                                        fontSize: "13px",
                                        transition: "all 0.15s ease"
                                    }}
                                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f4f4f4"; }}
                                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                                >
                                    <Icon style={{ fontSize: "17px", flexShrink: 0 }} />
                                    {label}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Main list panel ── */}
                <div style={{ flex: 1, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>

                    {/* Assets */}
                    {whereWeAt === "assets" && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 10px", flexShrink: 0 }}>
                                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Assets</span>
                                <SearchBar value={search} onChange={e => setSearch(e.target.value)} onSearch={submitSearch} onReset={() => { setSearch(""); setAssets(originalAssets); }} />
                            </div>
                            <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
                            {/* Table header */}
                            <div style={{ display: "flex", padding: "7px 16px", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#7c7c7c", flexShrink: 0 }}>
                                <span style={{ flex: 1 }}>Asset ID</span>
                                <span style={{ width: "70px" }}>Actions</span>
                            </div>
                            <div style={{ flex: 1, overflowY: "auto" }}>
                                {assets.length > 0 ? assets.map(asset => {
                                    const isSelected = selectedItem === asset.assetId;
                                    const isHovered  = hoveredItem === asset.assetId;
                                    return (
                                        <div
                                            key={asset.assetId}
                                            onClick={() => { setSelectedItem(asset.assetId); setDetailsHead(asset.assetId); fetchOneAssetDetails(asset.assetId); }}
                                            onMouseEnter={() => setHoveredItem(asset.assetId)}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f2f2f2", background: isSelected ? "rgba(38,144,68,0.05)" : isHovered ? "#fafafa" : "#fff", borderLeft: isSelected ? "3px solid #269044" : "3px solid transparent", cursor: "pointer", transition: "all 0.12s ease" }}
                                        >
                                            <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: isSelected ? "#269044" : "#202020" }}>{asset.assetId}</div>
                                            <div style={{ display: "flex", gap: "4px", opacity: isHovered || isSelected ? 1 : 0, transition: "opacity 0.15s" }}>
                                                <div style={{ color: "#269044", cursor: "pointer", padding: "3px", borderRadius: "4px" }} onClick={e => { e.stopPropagation(); downloadAssetById(asset.assetId); }} title="Download"><DownloadIcon style={{ fontSize: "18px" }} /></div>
                                                <div style={{ color: "#dc3545", cursor: "pointer", padding: "3px", borderRadius: "4px" }} onClick={e => { e.stopPropagation(); deleteAsset(asset.assetId); }} title="Delete"><DeleteIcon style={{ fontSize: "18px" }} /></div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ display: "flex", justifyContent: "center", paddingTop: "60px" }}>
                                        <CircularProgress size={36} sx={{ color: "#269044" }} />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Contracts */}
                    {whereWeAt === "contracts" && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 10px", flexShrink: 0 }}>
                                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Contracts</span>
                                <SearchBar value={search} onChange={e => setSearch(e.target.value)} onSearch={submitSearchContracts} onReset={() => { setSearch(""); setContracts(originalContracts); }} />
                            </div>
                            <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
                            <div style={{ display: "flex", padding: "7px 16px", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#7c7c7c", flexShrink: 0 }}>
                                <span style={{ flex: 1 }}>Contract ID</span>
                                <span style={{ width: "100px" }}>Policy</span>
                                <span style={{ width: "70px" }}>Actions</span>
                            </div>
                            <div style={{ flex: 1, overflowY: "auto" }}>
                                {contractsLoading ? (
                                    <div style={{ display: "flex", justifyContent: "center", paddingTop: "60px" }}><CircularProgress size={36} sx={{ color: "#269044" }} /></div>
                                ) : contractsError ? (
                                    <div style={{ textAlign: "center", color: "#dc3545", marginTop: "40px", fontSize: "13px" }}>Could not load contracts.</div>
                                ) : contracts.length > 0 ? contracts.map(contract => {
                                    const isSelected = selectedItem === contract['@id'];
                                    const isHovered  = hoveredItem === contract['@id'];
                                    return (
                                        <div
                                            key={contract['@id']}
                                            onClick={() => { setSelectedItem(contract['@id']); setDetailsHead(contract['@id']); fetchOneContractDetails(contract['@id']); }}
                                            onMouseEnter={() => setHoveredItem(contract['@id'])}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f2f2f2", background: isSelected ? "rgba(38,144,68,0.05)" : isHovered ? "#fafafa" : "#fff", borderLeft: isSelected ? "3px solid #269044" : "3px solid transparent", cursor: "pointer", transition: "all 0.12s ease" }}
                                        >
                                            <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: isSelected ? "#269044" : "#202020", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: "10px" }}>{contract['@id']}</div>
                                            <div style={{ width: "100px", fontSize: "12px", color: "#7c7c7c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contract["edc:contractPolicyId"] || "—"}</div>
                                            <div style={{ display: "flex", gap: "4px", width: "70px", justifyContent: "flex-end", opacity: isHovered || isSelected ? 1 : 0, transition: "opacity 0.15s" }}>
                                                <div style={{ color: "#269044", cursor: "pointer", padding: "3px" }} onClick={e => { e.stopPropagation(); downloadContractById(contract['@id']); }} title="Download"><DownloadIcon style={{ fontSize: "18px" }} /></div>
                                                <div style={{ color: "#dc3545", cursor: "pointer", padding: "3px" }} onClick={e => { e.stopPropagation(); deleteContract(contract['@id']); }} title="Delete"><DeleteIcon style={{ fontSize: "18px" }} /></div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ textAlign: "center", color: "#7c7c7c", marginTop: "40px", fontSize: "13px" }}>No contracts found.</div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Policies */}
                    {whereWeAt === "policies" && (
                        <>
                            <div style={{ padding: "12px 16px 10px", flexShrink: 0 }}>
                                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Policies</span>
                            </div>
                            <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
                            <div style={{ display: "flex", padding: "7px 16px", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#7c7c7c", flexShrink: 0 }}>
                                <span style={{ flex: 1 }}>Policy ID</span>
                                <span style={{ width: "50px" }}>Actions</span>
                            </div>
                            <div style={{ flex: 1, overflowY: "auto" }}>
                                {policies.length > 0 ? policies.map(policy => {
                                    const isSelected = selectedItem === policy['@id'];
                                    const isHovered  = hoveredItem === policy['@id'];
                                    return (
                                        <div
                                            key={policy['@id']}
                                            onClick={() => { setSelectedItem(policy['@id']); setDetailsHead(policy['@id']); fetchOnePolicyDetails(policy['@id']); }}
                                            onMouseEnter={() => setHoveredItem(policy['@id'])}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f2f2f2", background: isSelected ? "rgba(38,144,68,0.05)" : isHovered ? "#fafafa" : "#fff", borderLeft: isSelected ? "3px solid #269044" : "3px solid transparent", cursor: "pointer", transition: "all 0.12s ease" }}
                                        >
                                            <div style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: isSelected ? "#269044" : "#202020" }}>{policy['@id']}</div>
                                            <div style={{ opacity: isHovered || isSelected ? 1 : 0, transition: "opacity 0.15s" }}>
                                                <div style={{ color: "#269044", cursor: "pointer", padding: "3px" }} onClick={e => { e.stopPropagation(); downloadPolicyById(policy['@id']); }} title="Download"><DownloadIcon style={{ fontSize: "18px" }} /></div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ display: "flex", justifyContent: "center", paddingTop: "60px" }}><CircularProgress size={36} sx={{ color: "#269044" }} /></div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Data Transfer */}
                    {whereWeAt === "datatransfer" && (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 10px", flexShrink: 0 }}>
                                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>Data Transfer</span>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <div style={{ display: "flex", alignItems: "center", background: "#f5f5f5", borderRadius: "20px", padding: "2px 4px", border: "1px solid #e0e0e0" }}>
                                        <Radio checked={selectedValue === 'a'} onChange={handleRadioChange} value="a" size="small" sx={{ color: '#269044', '&.Mui-checked': { color: '#269044' }, padding: "3px" }} />
                                        <span style={{ fontSize: "12px", fontWeight: "500", color: selectedValue === 'a' ? "#269044" : "#7c7c7c", paddingRight: "6px", cursor: "pointer" }} onClick={() => handleRadioChange({ target: { value: 'a' } })}>By ID</span>
                                        <Radio checked={selectedValue === 'b'} onChange={handleRadioChange} value="b" size="small" sx={{ color: '#269044', '&.Mui-checked': { color: '#269044' }, padding: "3px" }} />
                                        <span style={{ fontSize: "12px", fontWeight: "500", color: selectedValue === 'b' ? "#269044" : "#7c7c7c", paddingRight: "6px", cursor: "pointer" }} onClick={() => handleRadioChange({ target: { value: 'b' } })}>By Originator</span>
                                    </div>
                                    <SearchBar value={search} onChange={e => setSearch(e.target.value)} onSearch={submitSearchDataTransfer} onReset={resetCatalog} />
                                </div>
                            </div>
                            <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />
                            {/* Table header */}
                            <div style={{ display: "flex", padding: "7px 16px", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "#7c7c7c", flexShrink: 0 }}>
                                <span style={{ flex: 3 }}>Asset ID</span>
                                <span style={{ flex: 1 }}>Originator ID</span>
                                <span style={{ width: "90px", textAlign: "right" }}>Status</span>
                            </div>
                            <div style={{ flex: 1, overflowY: "auto" }}>
                                {catalog.length > 0 ? catalog.map((item, index) => {
                                    const isDone     = isCatalogItemDone(item.asset?.assetId) || completedTransfers.includes(item.asset?.assetId);
                                    const isSelected = selectedItem === (item['@id'] ?? index);
                                    const isHovered  = hoveredItem === (item['@id'] ?? index);
                                    return (
                                        <div
                                            key={item['@id'] ?? index}
                                            onClick={() => {
                                                const id = item['@id'] ?? index;
                                                setSelectedItem(id);
                                                const assetId   = item.asset?.assetId || '';
                                                const originator = item.originatorId || item.participantId || 'Unknown';
                                                let status = "Not Initiated";
                                                if (completedTransfers.includes(assetId)) status = "Completed";
                                                Swal.fire({ title: 'Transfer Information', html: `<b>${assetId}</b><br/><br/><b>Originator:</b> ${originator}<br/><b>Status:</b> ${status}`, icon: 'info', confirmButtonText: 'OK' });
                                            }}
                                            onMouseEnter={() => setHoveredItem(item['@id'] ?? index)}
                                            onMouseLeave={() => setHoveredItem(null)}
                                            style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f2f2f2", background: isSelected ? "rgba(38,144,68,0.05)" : isHovered ? "#fafafa" : "#fff", borderLeft: isSelected ? "3px solid #269044" : "3px solid transparent", cursor: "pointer", transition: "all 0.12s ease" }}
                                        >
                                            <div style={{ flex: 3, fontSize: "13px", fontWeight: "500", color: "#202020" }}>{item.asset?.assetId}</div>
                                            <div style={{ flex: 1, fontSize: "12px", color: "#7c7c7c" }}>{item.originatorId || item.participantId || "Unknown"}</div>
                                            <div style={{ width: "90px", display: "flex", justifyContent: "flex-end" }}>
                                                {isDone ? (
                                                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "rgba(38,144,68,0.1)", color: "#269044", borderRadius: "10px", padding: "3px 9px", fontSize: "11px", fontWeight: "700" }}>
                                                        <VerifiedUserIcon style={{ fontSize: "13px" }} /> Done
                                                    </span>
                                                ) : (
                                                    <button
                                                        style={{ background: "#269044", color: "#fff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", transition: "background 0.15s" }}
                                                        onMouseEnter={e => e.currentTarget.style.background = "#1e7438"}
                                                        onMouseLeave={e => e.currentTarget.style.background = "#269044"}
                                                        onClick={e => { e.stopPropagation(); letsDoTheTransfer(e, item); }}
                                                    >
                                                        <ChangeCircleIcon style={{ fontSize: "13px" }} /> Transfer
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }) : catalogLoading ? (
                                    <div style={{ display: "flex", justifyContent: "center", paddingTop: "60px" }}><CircularProgress size={36} sx={{ color: "#269044" }} /></div>
                                ) : catalogError ? (
                                    <div style={{ textAlign: "center", color: "#dc3545", marginTop: "40px", fontSize: "13px" }}>Could not reach the EDC connector.</div>
                                ) : (
                                    <div style={{ textAlign: "center", color: "#7c7c7c", marginTop: "40px", fontSize: "13px" }}>No data offers available.</div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Details panel (hidden for datatransfer) ── */}
                {whereWeAt !== "datatransfer" && (
                    <div style={{ width: "360px", minWidth: "280px", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden", flexShrink: 0 }}>
                        <div style={{ padding: "14px 16px 10px", flexShrink: 0 }}>
                            <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c7c7c" }}>
                                {detailsHead ? "Details" : "Details"}
                            </span>
                        </div>
                        <div style={{ height: "1px", background: "#ebebeb", flexShrink: 0 }} />

                        {!details ? (
                            /* Empty state */
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "12px", paddingBottom: "60px" }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d8d8d8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                                </svg>
                                <div style={{ fontSize: "13px", fontWeight: "500", color: "#b0b0b0", textAlign: "center", lineHeight: "1.5" }}>
                                    Select an item from the list<br />to view its details
                                </div>
                            </div>
                        ) : (
                            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px" }}>
                                {detailsHead && (
                                    <div style={{ fontSize: "12px", fontWeight: "700", color: "#269044", background: "rgba(38,144,68,0.07)", border: "1px solid rgba(38,144,68,0.2)", borderRadius: "6px", padding: "6px 10px", marginBottom: "14px", wordBreak: "break-all" }}>
                                        {detailsHead}
                                    </div>
                                )}
                                {typeof details === 'string' ? (
                                    <div style={{ fontSize: "13px", color: "#7c7c7c" }}>{details}</div>
                                ) : (
                                    <DetailObject obj={details} />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    /* ── Fetch functions ─────────────────────────────────────────────────── */
    async function fetchAssets() {
        const res = await fetch(`/provider-api/api/management/wrapper/ui/pages/asset-page`, { method: "GET", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) { const d = await res.json(); setAssets(d.assets); setOriginalAssets(d.assets); }
    }

    async function fetchPolicies() {
        const res = await fetch(`/consumer-api/api/management/v2/policydefinitions/request`, { method: "POST", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) { setPolicies(await res.json()); }
    }

    async function fetchCatalog() {
        setCatalogLoading(true); setCatalogError(false);
        try {
            const res = await fetch(`/provider-api/api/management/wrapper/ui/pages/catalog-page/data-offers?connectorEndpoint=${consumerDspUrl}`, { method: "GET", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
            const myRes = await res.json();
            if (res.status === 200) { const items = Array.isArray(myRes) ? myRes : myRes?.dataOffers ?? []; setCatalog(items); setOriginalCatalog(items); }
            else setCatalogError(true);
        } catch { setCatalogError(true); } finally { setCatalogLoading(false); }
    }

    async function fetchContracts() {
        setContractsLoading(true); setContractsError(false);
        try {
            const res = await fetch(`/consumer-api/api/management/v2/contractdefinitions/request`, { method: "POST", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
            const myRes = await res.json();
            if (res.status === 200) { const items = Array.isArray(myRes) ? myRes : myRes?.contractDefinitions ?? []; setContracts(items); setOriginalContracts(items); }
            else setContractsError(true);
        } catch { setContractsError(true); } finally { setContractsLoading(false); }
    }

    async function fetchOneAssetDetails(assetId) {
        const res = await fetch(`/provider-api/api/management/v3/assets/${assetId}`, { method: "GET", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) setDetails(await res.json());
    }

    async function fetchOneContractDetails(contractId) {
        try {
            const res = await fetch(`/consumer-api/api/management/wrapper/ui/pages/contract-agreement-page`, { method: "POST", headers: { "x-api-key": "ApiKeyDefaultValue" } });
            const myRes = await res.json();
            if (res.status === 200) {
                const agreements = myRes.contractAgreements || [];
                const match = agreements.find(a => a.asset?.assetId === contractId) ?? agreements[0];
                setDetails(match ?? { info: "No agreement found", contractId });
            }
        } catch (err) { setDetails({ error: err.message }); }
    }

    async function fetchOnePolicyDetails(policyId) {
        const res = await fetch(`/consumer-api/api/management/wrapper/ui/pages/policy-page`, { method: "GET", headers: { "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) {
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.policyDefinitions ?? []);
            const match = list.find(p => p['@id'] === policyId || p.policyDefinitionId === policyId);
            setDetails(match ?? { policyId, note: "Policy fetched but ID not matched", raw: data });
        }
    }

    async function downloadAssetById(assetId) {
        const res = await fetch(`/provider-api/api/management/v3/assets/${assetId}`, { method: "GET", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) downloadJson(await res.json(), assetId);
    }

    async function downloadContractById(contractId) {
        const res = await fetch(`/consumer-legacy-api/api/management/v2/contractdefinitions/${contractId}`, { method: "GET", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) downloadJson(await res.json(), contractId);
    }

    async function downloadPolicyById(policyId) {
        const res = await fetch(`/consumer-api/api/management/wrapper/ui/pages/policy-page`, { method: "GET", headers: { "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 200) {
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.policyDefinitions ?? []);
            const match = list.find(p => p['@id'] === policyId || p.policyDefinitionId === policyId);
            if (match) downloadJson(match, policyId);
        }
    }

    async function deleteContract(contractId) {
        const res = await fetch(`/consumer-legacy-api/api/management/v2/contractdefinitions/${contractId}`, { method: "DELETE", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 204) { setChange(c => !c); Swal.fire({ title: 'Deletion Successful!', icon: 'success', confirmButtonText: 'OK' }); }
        else { const d = await res.json(); Swal.fire({ title: 'Error while deleting', text: d[0]?.message, icon: 'error', confirmButtonText: 'OK' }); }
    }

    async function deleteAsset(assetId) {
        const res = await fetch(`/consumer-legacy-api/api/management/v3/assets/${assetId}`, { method: "DELETE", headers: { "Content-Type": "application/json", "x-api-key": "ApiKeyDefaultValue" } });
        if (res.status === 204) { setChange(c => !c); Swal.fire({ title: 'Deletion Successful!', icon: 'success', confirmButtonText: 'OK' }); }
        else { const d = await res.json(); Swal.fire({ title: 'Error while deleting', text: d[0]?.message, icon: 'error', confirmButtonText: 'OK' }); }
    }

    function downloadJson(data, filename) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
        link.download = `${filename}.json`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }
}

export default DataSpaceSimple;
